import React, { useState, useEffect } from 'react';
import PlayerNameButton from '../../components/playerSheet/PlayerNameButton';
import { API_URLS } from '../../config/Constants';
import api from '../../config/axiosConfig';
import { sessionManager } from '../../config/sessionManager';
import { showErrorMsg, showSuccessMsg } from '../../config/common';

interface Player {
  id: number;
  _id: string;
  firstName: string;
  lastName: string;
  position: string;
  isPresent: boolean;
  inSeason: boolean; // exists in active season roster
  disabled: boolean; // checkbox should be disabled
  isGoalie: boolean;
}

interface PlayerRowProps {
  player: Player;
  isEven: boolean;
  seasonId?: string | null;
  onTogglePresence: (_id: string, checked: boolean) => void;
  disabled?: boolean;
}

const PlayerRow: React.FC<PlayerRowProps> = ({ player, isEven, seasonId, onTogglePresence, disabled = false }) => (
  <tr className={`${isEven ? 'bg-green-50' : ''} ${!player.inSeason ? 'opacity-50' : ''}`}>
    <td className="px-3 py-1 border-b text-center font-semibold">{player.id}</td>
    <td className="px-3 py-1 border-b">
      {player.inSeason && seasonId && player._id ? (
        <PlayerNameButton
          playerSeasonId={player._id}
          seasonId={seasonId}
          className="text-[#05396b] font-medium hover:underline"
        >
          {player.firstName} {player.lastName}
        </PlayerNameButton>
      ) : (
        <>
          {player.firstName} {player.lastName}
        </>
      )}
      {!player.inSeason && <span className="ml-2 text-xs text-gray-500 italic">(Inactive in season)</span>}
    </td>
    <td className="px-3 py-1 border-b">{player.position}</td>
    <td className="px-3 py-1 border-b text-center">
      <input
        type="checkbox"
        checked={player.isPresent}
        onChange={(e) => onTogglePresence(player._id, e.target.checked)}
        disabled={player.disabled || disabled}
        className={`w-5 h-5 ${
          !player.disabled && !disabled ? 'accent-green-600 cursor-pointer' : 'cursor-not-allowed'
        }`}
        title={
          player.disabled && !disabled
            ? 'Player is inactive in season'
            : disabled
              ? "You don't have permission to edit roster"
              : ''
        }
      />
    </td>
  </tr>
);

const RosterPage: React.FC = () => {
  const clientId = sessionManager.getClientId();
  const seasonId = sessionManager.getSeasonId();
  const gameId = sessionManager.getGameId();

  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [currentGoalieId, setCurrentGoalieId] = useState<string>('');
  const [seasonDefaultGoalieId, setSeasonDefaultGoalieId] = useState<string>('');
  const [savingGoalie, setSavingGoalie] = useState(false);

  // Fetch both game players and season players, then merge:
  //
  // Season | Game | → isPresent | disabled
  // -------+------+-------------+---------
  // active | yes  → true        | false   (Check)
  // inactive| yes → true        | true    (Check & Disable)
  // active | no   → false       | false   (Uncheck)
  // inactive| no  → don't show
  const fetchPlayers = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!clientId) {
        setError('No client ID found in session. Please select a client first.');
        setLoading(false);
        return;
      }

      const [gameRes, seasonRes, gameInfoRes, seasonInfoRes] = await Promise.all([
        api.get(`${API_URLS.GET_GAME_PLAYER}?clientId=${clientId}&seasonId=${seasonId}&gameId=${gameId}`),
        api.get(`${API_URLS.GET_SEASON_PLAYER}?clientId=${clientId}&seasonId=${seasonId}`),
        api.get(`${API_URLS.GET_GAME_BY_ID}/${gameId}`, { params: { clientId } }),
        api.get(`${API_URLS.GET_SEASON_BY_ID}/${seasonId}`, { params: { clientId } }),
      ]);

      if (!gameRes.data.success) {
        setError('Failed to fetch game roster');
        return;
      }
      if (!seasonRes.data.success) {
        setError('Failed to fetch season roster');
        return;
      }

      const gamePlayers: any[] = gameRes.data.data;
      const seasonPlayers: any[] = seasonRes.data.data;

      // Build a lookup of season players by their _id (which is what game_players.playerId references)
      const seasonMap = new Map<string, any>();
      for (const sp of seasonPlayers) {
        seasonMap.set(sp._id.toString(), sp);
      }

      // Build a set of playerIds that are in the game
      const gamePlayerIds = new Set(gamePlayers.map((gp: any) => gp.playerId.toString()));

      const merged: Player[] = [];

      // 1. Process all game players
      for (const gp of gamePlayers) {
        const spId = gp.playerId.toString();
        const sp = seasonMap.get(spId);
        const inSeason = sp ? sp.active !== false : false;
        const positions = Array.isArray(gp.position) ? gp.position : gp.position ? [gp.position] : [];

        merged.push({
          id: gp.no,
          _id: gp.playerId,
          firstName: gp.firstName || '',
          lastName: gp.lastName || '',
          position: Array.isArray(gp.position) ? gp.position.join(', ') : gp.position || '',
          isPresent: true,
          inSeason,
          disabled: !inSeason,
          isGoalie: positions.some((pos: string) => pos === 'Goalie'),
        });
      }

      // 2. Add active season players not in the game
      for (const sp of seasonPlayers) {
        const spId = sp._id.toString();
        if (gamePlayerIds.has(spId)) continue; // already handled above

        const isActive = sp.active !== false;
        if (!isActive) continue; // Season=False, Game=No → don't show

        const positions = Array.isArray(sp.position) ? sp.position : sp.position ? [sp.position] : [];

        merged.push({
          id: sp.no,
          _id: spId,
          firstName: sp.firstName || '',
          lastName: sp.lastName || '',
          position: Array.isArray(sp.position) ? sp.position.join(', ') : sp.position || '',
          isPresent: false,
          inSeason: true,
          disabled: false,
          isGoalie: positions.some((pos: string) => pos === 'Goalie'),
        });
      }

      // Sort: active (inSeason) first, then by jersey number
      merged.sort((a, b) => {
        if (a.inSeason !== b.inSeason) return a.inSeason ? -1 : 1;
        return a.id - b.id;
      });

      setPlayers(merged);
      setHasChanges(false);

      const seasonDefault = seasonInfoRes.data?.success
        ? seasonInfoRes.data.data?.startingGoalieId || ''
        : '';
      setSeasonDefaultGoalieId(seasonDefault);

      if (gameInfoRes.data.success && gameInfoRes.data.data) {
        setCurrentGoalieId(gameInfoRes.data.data.currentGoalieId || '');
      } else {
        setCurrentGoalieId('');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch players');
      console.error('Error fetching players:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlayers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="w-full bg-white mb-6 mt-4">
        <div className="flex items-center justify-center py-8">
          <div className="text-lg">Loading players...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full bg-white mb-6 mt-4">
        <div className="flex items-center justify-center py-8">
          <div className="text-red-600 text-lg">Error: {error}</div>
        </div>
      </div>
    );
  }

  const handleTogglePresence = (_id: string, checked: boolean) => {
    setPlayers((prev) => {
      const updated = prev.map((p) => (p._id === _id ? { ...p, isPresent: checked } : p));
      return updated;
    });
    setHasChanges(true);
  };

  // Select All only affects enabled (inSeason) players
  const handleSelectAll = (checked: boolean) => {
    setPlayers((prev) => prev.map((p) => (p.inSeason && !p.disabled ? { ...p, isPresent: checked } : p)));
    setHasChanges(true);
  };

  const enabledPlayers = players.filter((p) => !p.disabled);
  const allEnabledSelected = enabledPlayers.length > 0 && enabledPlayers.every((p) => p.isPresent);
  const goalieOptions = players.filter((p) => p.isPresent && p.isGoalie);

  const displayedGoalieId = currentGoalieId || seasonDefaultGoalieId;

  const handleUpdateGameGoalie = async (value: string) => {
    if (!clientId || !gameId) return;
    const previousCurrent = currentGoalieId;
    setCurrentGoalieId(value);

    try {
      setSavingGoalie(true);
      const response = await api.post(`${API_URLS.UPDATE_GAME_GOALIE}/${gameId}/goalie`, {
        clientId,
        currentGoalieId: value || null,
      });
      if (response.data.success) {
        setCurrentGoalieId(response.data.data.currentGoalieId || '');
        showSuccessMsg('Goalie updated');
      } else {
        setCurrentGoalieId(previousCurrent);
        showErrorMsg(response.data.message || 'Failed to update goalie');
      }
    } catch (err: any) {
      setCurrentGoalieId(previousCurrent);
      showErrorMsg(err.response?.data?.message || err.message || 'Failed to update goalie');
    } finally {
      setSavingGoalie(false);
    }
  };

  const handleUpdateGameRoster = async () => {
    try {
      setLoading(true);

      const payload = players
        .filter((p) => p.isPresent)
        .map((p) => ({
          playerId: p._id,
          clientId,
          seasonId,
          gameId,
          include: true,
        }));

      await api.post(API_URLS.UPDATE_GAME_PLAYER, payload);
      setHasChanges(false);

      showSuccessMsg('Game roster updated successfully!');

      await fetchPlayers();
    } catch (err: any) {
      showErrorMsg(err.response?.data?.message || err.message || 'Failed to update game roster');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full px-4 py-6 sm:px-6">
      <div className="flex flex-col justify-between text-sd-navy text-2xl sm:text-3x font-bold mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-sd-navy">Team Roster</h1>
        <p className="mt-2 text-sm sm:text-base text-sd-navy font-medium max-w-3xl break-all">
          Players assigned to this game roster. Inactive players are grayed out. If no game goalie is set, the season
          goalie is shown until you change it.
        </p>
      </div>

      <div className="mb-6 flex flex-row items-end justify-between gap-3">
        <div className="min-w-0 flex-1 max-w-xl">
          <label className="block text-sm font-semibold text-sd-navy mb-2">Goalie</label>
          <select
            value={displayedGoalieId}
            onChange={(e) => handleUpdateGameGoalie(e.target.value)}
            disabled={savingGoalie || goalieOptions.length === 0}
            className="w-full px-4 py-2 border border-[#22c55e] rounded bg-sd-light focus:outline-none disabled:opacity-60"
          >
            <option value="">Select goalie...</option>
            {goalieOptions.map((g) => (
              <option key={g._id} value={g._id}>
                #{g.id} {g.firstName} {g.lastName}
                {!currentGoalieId && seasonDefaultGoalieId === g._id ? ' (season default)' : ''}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={() => handleSelectAll(!allEnabledSelected)}
          className="shrink-0 font-semibold py-2 px-6 rounded-md transition-colors duration-200 bg-sd-navy text-white hover:bg-sd-navy cursor-pointer"
        >
          {allEnabledSelected ? 'Deselect All' : 'Select All'}
        </button>
      </div>

      <div className="bg-sd-navy shadow-md text-white text-xl font-semibold px-4 py-2">Team Roster</div>
      <div className="overflow-x-auto">
        <table className="min-w-full border bg-white border-sd-line rounded-lg">
          <thead>
            <tr className="bg-[#f4fafd]">
              <th className="px-3 py-2 border-b text-center">#</th>
              <th className="px-3 py-2 border-b text-left">Name</th>
              <th className="px-3 py-2 border-b text-left">Position</th>
              <th className="px-3 py-2 border-b text-center">Playing</th>
            </tr>
          </thead>
          <tbody>
            {players.map((player, index) => (
              <PlayerRow
                key={player._id}
                player={player}
                isEven={index % 2 === 0}
                seasonId={seasonId}
                onTogglePresence={handleTogglePresence}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Update Game Roster Button */}
      <div className="flex justify-center mt-6">
        <button
          onClick={handleUpdateGameRoster}
          disabled={!hasChanges || loading}
          className={`font-semibold py-3 px-6 rounded-md transition-colors duration-200 ${
            hasChanges && !loading ? 'bg-sd-accent cursor-pointer' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
          title={hasChanges ? 'Save roster changes' : 'No changes to save'}
        >
          {loading ? 'Updating...' : 'Update Game Roster'}
        </button>
      </div>
    </div>
  );
};

export default RosterPage;
